import { readonly, ref } from 'vue'

/**
 * 📱 M6 (mobile gameplan): is the APP in the foreground?
 *
 * The website asks document.visibilityState, and every one of those listeners
 * stays. Inside the native shell that signal is not enough to bet a fight on:
 * Android keeps a backgrounded WebView's JavaScript running, so a board whose
 * WebView never fired visibilitychange would keep expiring missions in a
 * pocket. The shell's own appStateChange (@capacitor/app, wired in
 * plugins/native-shell.client.ts) reports here instead. On the web nothing
 * reports, and this stays true.
 *
 * Who listens: the node screen's clock (and the pause veil it raises for the
 * way back), the music latch and the AudioContext (useMusic), the save flush
 * (plugins/persistence.client.ts), the cloud push and the run pull
 * (useCloudSync, useRunSync), the run clock (SessionClock) and the boss
 * card's real-time skill clock (MissionDetail).
 */
const active = ref(true)
const listeners = new Set<(active: boolean) => void>()

/** The shell reports a change here (and window.__edAppState, in the suites). */
export function setAppActive(next: boolean) {
  if (active.value === next) return
  active.value = next
  for (const listener of [...listeners]) {
    try { listener(next) } catch (error) { console.warn('An app state listener failed.', error) }
  }
}

/** Calls `listener` on every change, in the order they were added; returns the unsubscribe. */
export function onAppStateChange(listener: (active: boolean) => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export function useAppLifecycle() {
  return { appActive: readonly(active) }
}
